/**
 * All filtering and sorting. Runs entirely in the browser over the merged in-memory
 * list — no server round-trip for any of it.
 */

import { deadlinePassed, eventFinished, parseDate } from './dates.js';

export const RADIUS_PRESETS = [
  { id: '25', label: '25 mi', miles: 25 },
  { id: '100', label: '100 mi', miles: 100 },
  { id: 'state', label: 'Statewide', miles: null },
  { id: 'anywhere', label: 'Anywhere', miles: Infinity },
];

export const FORMAT_OPTIONS = [
  { id: 'in-person', label: 'In-person' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'online', label: 'Online' },
];

export const SORT_OPTIONS = [
  { id: 'deadline', label: 'Deadline soonest' },
  { id: 'distance', label: 'Distance' },
  { id: 'prize', label: 'Prize amount' },
  { id: 'date', label: 'Start date' },
];

export const DEFAULT_FILTERS = {
  radius: 'anywhere',
  formats: ['in-person', 'hybrid', 'online'],
  dateFrom: '',
  dateTo: '',
  themes: [],
  sort: 'deadline',
  query: '',
};

/**
 * Pull a comparable number out of a prize string like "$20,000" or "₹ 200,000".
 *
 * NOTE: currencies are deliberately NOT converted. Doing it properly needs live FX
 * rates, which means a paid API — out of scope for a zero-cost build. Sorting by prize
 * therefore ranks by raw magnitude, so a ₹200,000 prize outranks a $20,000 one. The UI
 * shows the original string with its symbol so the user can see what they're comparing.
 */
export function prizeValue(prizeSummary) {
  if (!prizeSummary) return null;
  const match = String(prizeSummary).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const value = Number.parseFloat(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

const normalizeState = (value) =>
  String(value || '').toLowerCase().replace(/[^a-z]/g, '');

/**
 * Radius test.
 *
 * Online events are ALWAYS shown regardless of radius — that's the whole point of an
 * online event, and geography can't exclude it. In-person and hybrid events are tested
 * against the selected radius.
 */
function withinRadius(hackathon, filters, userLocation) {
  if (hackathon.format === 'online') return true;
  if (filters.radius === 'anywhere') return true;
  if (!userLocation) return true; // no location known => radius is meaningless, show everything

  if (filters.radius === 'state') {
    const userState = normalizeState(userLocation.state);
    if (!userState) return true; // we don't know the user's state; don't hide anything
    return normalizeState(hackathon.venue_state) === userState;
  }

  const preset = RADIUS_PRESETS.find((p) => p.id === filters.radius);
  const limit = preset ? preset.miles : Infinity;
  if (!Number.isFinite(limit)) return true;

  // Unlocated in-person events can't be tested. They're excluded here and counted
  // separately so the UI can tell the user how many were set aside, rather than
  // silently dropping them.
  if (hackathon.distance_mi == null) return false;
  return hackathon.distance_mi <= limit;
}

function withinDateRange(hackathon, filters) {
  const start = parseDate(hackathon.start_date);
  const end = parseDate(hackathon.end_date) || start;
  if (!start) return true;

  if (filters.dateFrom) {
    const from = parseDate(`${filters.dateFrom}T00:00:00Z`);
    if (from && end && end < from) return false;
  }
  if (filters.dateTo) {
    const to = parseDate(`${filters.dateTo}T23:59:59Z`);
    if (to && start > to) return false;
  }
  return true;
}

function matchesThemes(hackathon, filters) {
  if (!filters.themes || filters.themes.length === 0) return true;
  const themes = (hackathon.themes || []).map((t) => t.toLowerCase());
  return filters.themes.some((wanted) => themes.includes(wanted.toLowerCase()));
}

function matchesQuery(hackathon, filters) {
  const query = (filters.query || '').trim().toLowerCase();
  if (!query) return true;
  return [hackathon.name, hackathon.venue_city, hackathon.venue_state, hackathon.source_platform]
    .filter(Boolean)
    .some((field) => field.toLowerCase().includes(query));
}

function compare(a, b, sort) {
  switch (sort) {
    case 'distance': {
      // Nulls last, regardless of direction.
      if (a.distance_mi == null && b.distance_mi == null) return 0;
      if (a.distance_mi == null) return 1;
      if (b.distance_mi == null) return -1;
      return a.distance_mi - b.distance_mi;
    }
    case 'prize': {
      const av = prizeValue(a.prize_summary);
      const bv = prizeValue(b.prize_summary);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return bv - av; // largest first
    }
    case 'date': {
      const av = Date.parse(a.start_date) || Infinity;
      const bv = Date.parse(b.start_date) || Infinity;
      return av - bv;
    }
    case 'deadline':
    default: {
      // Events with a real deadline rank above those without one; TBA sinks to the bottom.
      const av = Date.parse(a.registration_deadline) || Infinity;
      const bv = Date.parse(b.registration_deadline) || Infinity;
      if (av === bv) return (Date.parse(a.start_date) || 0) - (Date.parse(b.start_date) || 0);
      return av - bv;
    }
  }
}

/**
 * Apply every filter and the chosen sort.
 * Returns the visible list plus the counts the UI needs to explain what it hid.
 */
export function applyFilters(hackathons, filters, userLocation, now = Date.now()) {
  let expiredCount = 0;
  let unlocatedHidden = 0;

  const visible = hackathons.filter((h) => {
    // Auto-hide anything whose registration deadline has passed, or that has already
    // finished — a hackathon you cannot attend is noise, not a result.
    if (deadlinePassed(h, now) || eventFinished(h, now)) {
      expiredCount += 1;
      return false;
    }

    if (!filters.formats.includes(h.format)) return false;
    if (!withinDateRange(h, filters)) return false;
    if (!matchesThemes(h, filters)) return false;
    if (!matchesQuery(h, filters)) return false;

    if (!withinRadius(h, filters, userLocation)) {
      if (h.format !== 'online' && h.distance_mi == null) unlocatedHidden += 1;
      return false;
    }
    return true;
  });

  visible.sort((a, b) => compare(a, b, filters.sort));

  return { visible, expiredCount, unlocatedHidden };
}

/** Every theme present in the data, with counts, for building the theme filter. */
export function collectThemes(hackathons) {
  const counts = new Map();
  for (const h of hackathons) {
    for (const theme of h.themes || []) {
      counts.set(theme, (counts.get(theme) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([theme, count]) => ({ theme, count }));
}
